import { useEffect, useState } from 'react';
import { Title, useCreatePath, useGetRecordId } from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { PreOrderForm } from '../../pre-order/PreOrderForm';
import { toDateTimeLocalValue } from '../../shared/clientTime';
import type { PreOrderInput } from '../../pre-order/preOrderApi';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { InquiryAvailability } from './InquiryAvailability';
import { InquiryQuotePanel } from './InquiryQuotePanel';
import { MessageDraftDialog } from './MessageDraftDialog';
import { ReasonDialog } from './ReasonDialog';
import { statusColor } from './InquiryList';
import {
  DECLINE_REASON_HINTS,
  FEATURE_INQUIRIES,
  MIN_REASON_LENGTH,
  REJECTION_REASON_HINTS,
  confirmationSent,
  declineInquiry,
  fetchConfirmationDraft,
  fetchInquiry,
  forwardToFtl,
  ftlRespond,
  locateInquiry,
  recordOutcome,
  toErrorMessage,
  tradeSignal,
  updateInquiry,
  type InquiryDetail,
} from './inquiriesApi';

type Dialogs =
  | null
  | 'edit'
  | 'forward'
  | 'ftlPossible'
  | 'ftlNotPossible'
  | 'trade'
  | 'decline'
  | 'confirmation'
  | 'rejected'
  | 'no_response'
  | 'accepted';

const place = (country: string | null, postal: string | null, city: string | null) =>
  [country, postal, city].filter(Boolean).join(' ') || '—';

/** Wartości formularza z nagłówka zapytania — te same pola co prezlecenie. */
export function formValuesOf(d: InquiryDetail): PreOrderInput {
  const h = d.header;
  return {
    companyId: h.companyId,
    clientName: h.clientName,
    loadingCity: h.loadingCity,
    loadingCountry: h.loadingCountry,
    loadingPostalCode: h.loadingPostalCode,
    unloadingCity: h.unloadingCity,
    unloadingCountry: h.unloadingCountry,
    unloadingPostalCode: h.unloadingPostalCode,
    loadingDate: h.loadingDate ? toDateTimeLocalValue(h.loadingDate) : null,
    unloadingDate: h.unloadingDate ? toDateTimeLocalValue(h.unloadingDate) : null,
    requiredVehicleType: h.requiredVehicleType,
    cargo: h.cargo,
    weight: h.weight,
    pallets: h.pallets,
  };
}

/**
 * Karta zapytania klienta (26.10.2026, P3; makieta: „Dostępność aut dla
 * zapytania”, „Wycena”, wynik oferty). Jeden rekord: parametry, dostępność
 * aut, decyzja CS (przekaż do FTL, sygnał do Trade, odmowa, potwierdzenie
 * dla klienta), wycena z zatwierdzeniem przez Trade, wynik oferty — przyjęta
 * zamienia zapytanie w prezlecenie w tym samym rekordzie.
 */
export const InquiryShow = () => {
  const on = useFeatureState(FEATURE_INQUIRIES);
  const id = useGetRecordId();
  const createPath = useCreatePath();
  const [detail, setDetail] = useState<InquiryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialogs>(null);
  const [form, setForm] = useState<PreOrderInput>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (on !== true || id === undefined) return;
    let alive = true;
    fetchInquiry(id)
      .then((d) => alive && setDetail(d))
      .catch((e) => alive && setError(toErrorMessage(e)));
    return () => {
      alive = false;
    };
  }, [on, id]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;
  if (error && !detail) return <Alert severity="error">{error}</Alert>;
  if (!detail) return <FeaturesLoading />;

  const d = detail;
  const h = d.header;
  const inq = d.inquiry;
  const a = d.actions;
  const close = () => setDialog(null);
  const run = (fn: () => Promise<InquiryDetail>) =>
    void (async () => {
      setBusy(true);
      try {
        setDetail(await fn());
        setError(null);
      } catch (e) {
        setError(toErrorMessage(e));
      } finally {
        setBusy(false);
      }
    })();

  return (
    <Box sx={{ p: 1, display: 'grid', gap: 1.5 }} data-testid="inquiry-show">
      <Title title={`Zapytanie ${d.ref}`} />
      {error && <Alert severity="error">{error}</Alert>}

      {!d.isInquiry && (
        <Alert severity="success" data-testid="cs-wycena-prezlecenie">
          Oferta przyjęta — w tym samym rekordzie jest teraz prezlecenie. Dalej pracuje się na prezleceniu (uzupełnienie i
          przekazanie do Administracji).{' '}
          <Link to={createPath({ resource: 'pre-orders', type: 'show', id: d.id })}>Otwórz prezlecenie</Link>
        </Alert>
      )}

      <SectionCard title={`Zapytanie ${d.ref}`}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Chip label={inq.statusLabel} color={statusColor(inq.status)} data-testid="inquiry-status" />
          <Typography variant="caption" color="text.secondary">
            od {dateTimeNoSeconds(inq.statusSince)}
            {inq.statusBy ? ` · ${inq.statusBy}` : ''}
            {inq.statusReason ? ` · ${inq.statusReason}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))' }}>
          <Typography variant="body2">
            <b>Klient:</b> {h.contractorName ?? h.clientName ?? '—'}
          </Typography>
          <Typography variant="body2">
            <b>Spółka:</b> {h.companyName ?? '—'}
          </Typography>
          <Typography variant="body2">
            <b>Załadunek:</b> {place(h.loadingCountry, h.loadingPostalCode, h.loadingCity)},{' '}
            {h.loadingDate ? dateTimeNoSeconds(h.loadingDate) : '—'}
          </Typography>
          <Typography variant="body2">
            <b>Rozładunek:</b> {place(h.unloadingCountry, h.unloadingPostalCode, h.unloadingCity)},{' '}
            {h.unloadingDate ? dateTimeNoSeconds(h.unloadingDate) : '—'}
          </Typography>
          <Typography variant="body2">
            <b>Pojazd:</b> {h.requiredVehicleType ?? '—'}
          </Typography>
          <Typography variant="body2">
            <b>Waga:</b> {h.weight ? `${h.weight.toLocaleString('pl-PL')} kg` : '—'}
          </Typography>
          <Typography variant="body2">
            <b>Skąd zapytanie:</b> {inq.sourceNote ?? '—'}
          </Typography>
          <Typography variant="body2">
            <b>Przyjął:</b> {h.createdBy ?? '—'}, {dateTimeNoSeconds(h.createdAt)}
          </Typography>
        </Box>
        {inq.points.note && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Punkty do dolotu: {inq.points.note}
          </Alert>
        )}
        {a.edit && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              onClick={() => {
                setForm(formValuesOf(d));
                setDialog('edit');
              }}
              data-testid="inquiry-edit"
            >
              Popraw dane
            </Button>
            {(!inq.points.from || !inq.points.to) && (
              <Button size="small" onClick={() => run(() => locateInquiry(d.id))} disabled={busy}>
                Wyznacz punkty z kodu pocztowego
              </Button>
            )}
          </Stack>
        )}
      </SectionCard>

      {d.isInquiry && <InquiryAvailability detail={d} onChange={setDetail} />}

      <SectionCard title="Decyzja">
        <Box data-testid="inquiry-decision">
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {a.confirmationSent && (
              <Button variant="contained" onClick={() => setDialog('confirmation')} data-testid="inquiry-confirm-client">
                Potwierdź klientowi możliwość realizacji
              </Button>
            )}
            {a.forwardToFtl && (
              <Button variant="outlined" onClick={() => setDialog('forward')} data-testid="inquiry-forward-ftl">
                {inq.forwardedToFtl ? 'Przekaż do FTL ponownie' : 'Przekaż do FTL do decyzji'}
              </Button>
            )}
            {a.tradeSignal && (
              <Button variant="outlined" onClick={() => setDialog('trade')} data-testid="inquiry-trade-signal">
                Sygnał: lepiej Trade
              </Button>
            )}
            {a.decline && (
              <Button variant="outlined" color="error" onClick={() => setDialog('decline')} data-testid="inquiry-decline">
                Odmów z powodem
              </Button>
            )}
          </Box>
          <Box sx={{ mt: 1, display: 'grid', gap: 0.5 }}>
            {inq.confirmation && (
              <Typography variant="body2" data-testid="inquiry-confirmation-sent">
                Potwierdzenie wysłane ręcznie: {inq.confirmation.by ?? '—'}, {dateTimeNoSeconds(inq.confirmation.at)}
              </Typography>
            )}
            {inq.forwardedToFtl && (
              <Typography variant="body2" data-testid="inquiry-forwarded">
                Przekazane do FTL: {inq.forwardedToFtl.by ?? '—'}, {dateTimeNoSeconds(inq.forwardedToFtl.at)}
                {inq.forwardedToFtl.note ? ` — ${inq.forwardedToFtl.note}` : ''}
                {inq.forwardedToFtl.responseLabel
                  ? ` · odpowiedź FTL: ${inq.forwardedToFtl.responseLabel}${inq.forwardedToFtl.responseNote ? ` (${inq.forwardedToFtl.responseNote})` : ''}, ${inq.forwardedToFtl.responseBy ?? ''}`
                  : ' · czeka na odpowiedź FTL'}
              </Typography>
            )}
            {a.ftlRespond && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" onClick={() => setDialog('ftlPossible')} data-testid="inquiry-ftl-possible">
                  FTL: możliwe
                </Button>
                <Button size="small" variant="outlined" onClick={() => setDialog('ftlNotPossible')} data-testid="inquiry-ftl-not-possible">
                  FTL: niemożliwe
                </Button>
              </Box>
            )}
            {inq.tradeSignal && (
              <Typography variant="body2">
                Sygnał do Trade: {inq.tradeSignal.by ?? '—'}, {dateTimeNoSeconds(inq.tradeSignal.at)}
                {inq.tradeSignal.note ? ` — ${inq.tradeSignal.note}` : ''}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Wstępne wskazanie nie blokuje pojazdu. Ostatecznie obsadza Master FTL po zapisie zlecenia.
          </Typography>
        </Box>
      </SectionCard>

      {d.features.quotes && (d.isInquiry || d.quotes.length > 0) && <InquiryQuotePanel detail={d} onChange={setDetail} />}

      {d.features.outcomes && d.isInquiry && (
        <SectionCard title="Wynik oferty">
          <Box data-testid="inquiry-outcome">
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="success"
                disabled={!a.outcome}
                onClick={() => setDialog('accepted')}
                data-testid="cs-wycena-wynik-przyjeta"
              >
                przyjęta
              </Button>
              <Button disabled={!a.outcome} variant="outlined" onClick={() => setDialog('rejected')} data-testid="cs-wycena-wynik-odrzucona">
                odrzucona
              </Button>
              <Button disabled={!a.outcome} variant="outlined" onClick={() => setDialog('no_response')} data-testid="cs-wycena-wynik-brak-odpowiedzi">
                brak odpowiedzi
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Wynik zapisuje się po wysłaniu oferty albo potwierdzenia. Przyjęta tworzy prezlecenie w tym samym rekordzie.
            </Typography>
          </Box>
        </SectionCard>
      )}

      {/* ── Okna ─────────────────────────────────────────────── */}
      <Dialog open={dialog === 'edit'} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>Popraw dane zapytania</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Zmiana danych po wycenie unieważnia wycenę — Trade zatwierdzał inną treść.
          </Typography>
          <PreOrderForm values={form} onChange={(f, v) => setForm((x) => ({ ...x, [f]: v }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Anuluj</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => {
              run(() => updateInquiry(d.id, form));
              close();
            }}
            data-testid="inquiry-edit-save"
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      <ReasonDialog
        open={dialog === 'forward'}
        title="Przekaż do FTL do decyzji"
        label="Notatka dla planisty (opcjonalnie)"
        confirmLabel="Przekaż"
        description="Planista FTL zobaczy zapytanie na liście „Przekazane do FTL” i odpowie: możliwe albo niemożliwe."
        onClose={close}
        onConfirm={async (t) => setDetail(await forwardToFtl(d.id, t || undefined))}
        confirmTestId="inquiry-forward-confirm"
      />
      <ReasonDialog
        open={dialog === 'ftlPossible'}
        title="FTL: możliwe"
        label="Notatka (np. które auto)"
        confirmLabel="Zapisz odpowiedź"
        onClose={close}
        onConfirm={async (t) => setDetail(await ftlRespond(d.id, 'possible', t || undefined))}
      />
      <ReasonDialog
        open={dialog === 'ftlNotPossible'}
        title="FTL: niemożliwe"
        label="Powód"
        minLength={MIN_REASON_LENGTH}
        confirmLabel="Zapisz odpowiedź"
        onClose={close}
        onConfirm={async (t) => setDetail(await ftlRespond(d.id, 'not_possible', t))}
      />
      <ReasonDialog
        open={dialog === 'trade'}
        title="Sygnał: lepiej Trade"
        label="Dlaczego (opcjonalnie)"
        hints={['brak bezpiecznej opcji floty']}
        confirmLabel="Zgłoś do Trade"
        description="Trade zobaczy zapytanie na liście z filtrem „Sygnał do Trade”."
        onClose={close}
        onConfirm={async (t) => setDetail(await tradeSignal(d.id, t || undefined))}
      />
      <ReasonDialog
        open={dialog === 'decline'}
        title="Odmów klientowi"
        label="Powód odmowy"
        hints={DECLINE_REASON_HINTS}
        minLength={MIN_REASON_LENGTH}
        confirmLabel="Odmów"
        description="Odmowę przekazujesz klientowi sam. Zapytanie zostanie zamknięte."
        onClose={close}
        onConfirm={async (t) => setDetail(await declineInquiry(d.id, t))}
        confirmTestId="inquiry-decline-confirm"
      />
      <ReasonDialog
        open={dialog === 'rejected'}
        title="Wynik oferty: odrzucona"
        label="Powód"
        hints={REJECTION_REASON_HINTS}
        minLength={MIN_REASON_LENGTH}
        confirmLabel="Zapisz wynik"
        onClose={close}
        onConfirm={async (t) => setDetail(await recordOutcome(d.id, 'rejected', t))}
        confirmTestId="inquiry-outcome-confirm"
      />
      <ReasonDialog
        open={dialog === 'no_response'}
        title="Wynik oferty: brak odpowiedzi"
        label="Powód / co ustalono"
        hints={['brak odpowiedzi klienta']}
        minLength={MIN_REASON_LENGTH}
        confirmLabel="Zapisz wynik"
        onClose={close}
        onConfirm={async (t) => setDetail(await recordOutcome(d.id, 'no_response', t))}
      />
      <ReasonDialog
        open={dialog === 'accepted'}
        title="Wynik oferty: przyjęta"
        label="Notatka (opcjonalnie)"
        confirmLabel="Zapisz wynik"
        description="Powstanie prezlecenie w tym samym rekordzie, z danymi zapytania i ceną zatwierdzoną. Prezlecenie uzupełnia CS i przekazuje do Administracji jak każde inne."
        onClose={close}
        onConfirm={async (t) => setDetail(await recordOutcome(d.id, 'accepted', t || undefined))}
        confirmTestId="inquiry-outcome-confirm"
      />
      <MessageDraftDialog
        open={dialog === 'confirmation'}
        title={`Potwierdzenie dla klienta — ${d.ref}`}
        load={(language) => fetchConfirmationDraft(d.id, language)}
        onClose={close}
        onSent={async (subject, body) => setDetail(await confirmationSent(d.id, subject, body))}
        sentTestId="inquiry-confirmation-mark-sent"
      />
    </Box>
  );
};
